import { forwardRef, useState, useEffect } from 'react';

const desserts = [
  { name: 'San Sebastian', description: 'Karamelize yüzeyi ve kremsi dokusuyla klasik San Sebastian lezzeti', image: '/san-sebastian.png' },
  { name: 'Cheesecake Lotus', description: 'Lotus bisküvili cheesecake', image: '/cheesecake-lotus.png' },
  { name: 'Cheesecake Limon', description: 'Ferahlatıcı limon aromalı klasik cheesecake', image: '/cheesecake-limon.png' },
  { name: 'Cream Puff', description: 'Hafif hamur ve yoğun krema dolgusu ile hazırlanan zarif bir tatlı', image: '/cream-puff.png' },
  { name: 'Ekler', description: 'Çikolata kaplı, kremalı ve yumuşak dokulu ekler', image: '/ekler.png' },
  { name: 'Magnolya', description: 'Katmanlı yapısı ve mevsim meyveleriyle hazırlanan magnolya', image: '/magnolya.png' },
  { name: 'Tiramisu', description: 'Kahve ve mascarpone peynirli İtalyan klasiği', image: '/tiramisu.png' },
  { name: 'Profiterol', description: 'Krema dolgulu choux hamurları ve çikolata sosuyla hazırlanan profiterol', image: '/profiterol.png' },
  { name: 'Brownie', description: 'Yoğun çikolatalı, fudgy dokusuyla brownie', image: '/brownie.png' },
  { name: 'Sufle', description: 'Sıcak servis edilen çikolata sufle', image: '/sufle.png' },
  { name: 'Trileçe', description: 'Geleneksel Balkan tatlısı', image: '/trilece.png' },
  { name: 'Cookie', description: 'Taze pişmiş, yumuşak dokulu kurabiye çeşitleri', image: '/cookie.png' },
  { name: 'Vişneli Tart', description: 'Ekşi vişne dolgulu özel tart', image: '/visneli-tart.png' },
  { name: 'Elmalı Tart', description: 'Taze elma ve tarçınlı klasik tart', image: '/elmali-tart.png' }
];

const DessertsMenu = forwardRef<HTMLElement>((props, ref) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const itemsPerView = 4;
  const totalSlides = Math.ceil(desserts.length / itemsPerView);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
    setIsAutoPlaying(false);
  };

  return (
    <section
      ref={ref}
      className="relative py-12 px-[4%] opacity-0 transition-all duration-1000 bg-gradient-to-b from-[#EDE4D8] to-[#F5EDE4] overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-20 h-20 rounded-full bg-[#D4AF37]/10 blur-2xl"></div>
      <div className="absolute bottom-20 right-10 w-32 h-32 rounded-full bg-[#2B4C7E]/5 blur-3xl"></div>

      {/* Header */}
      <div className="text-center mb-10 relative z-10">
        <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-3 font-semibold">
          Tatlılarımız
        </p>
        <h2 className="font-playfair text-4xl lg:text-5xl text-[#1A365D] mb-2">
          Günlük Taze Üretim
        </h2>
        <div className="flex items-center justify-center gap-3 mt-4">
          <div className="w-20 h-[2px] bg-gradient-to-r from-transparent to-[#D4AF37]"></div>
          <i className="ri-cake-3-fill text-[#D4AF37] text-xl"></i>
          <div className="w-20 h-[2px] bg-gradient-to-l from-transparent to-[#D4AF37]"></div>
        </div>
      </div>

      {/* Slider Container */}
      <div className="relative max-w-[1400px] mx-auto">
        {/* Navigation Buttons */}
        <button
          onClick={prevSlide}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-6 z-20 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-white shadow-xl flex items-center justify-center text-[#1A365D] hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer group"
          aria-label="Previous"
        >
          <i className="ri-arrow-left-s-line text-xl lg:text-2xl group-hover:scale-110 transition-transform"></i>
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-6 z-20 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-white shadow-xl flex items-center justify-center text-[#1A365D] hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer group"
          aria-label="Next"
        >
          <i className="ri-arrow-right-s-line text-xl lg:text-2xl group-hover:scale-110 transition-transform"></i>
        </button>

        {/* Desserts Slider */}
        <div className="overflow-hidden px-2">
          <div
            className="flex gap-8 transition-transform duration-700 ease-in-out"
            style={{
              transform: `translateX(calc(-${currentIndex * 100}% - ${currentIndex * 2}rem))`,
            }}
          >
            {/* Render all slides */}
            {Array.from({ length: totalSlides }).map((_, slideIndex) => {
              const slideItems = desserts.slice(slideIndex * itemsPerView, (slideIndex + 1) * itemsPerView);
              const isLastSlideWithFewerItems = slideIndex === totalSlides - 1 && slideItems.length < itemsPerView;

              return (
                <div
                  key={slideIndex}
                  className={`w-full flex-shrink-0 gap-6 ${
                    isLastSlideWithFewerItems
                      ? 'flex justify-center flex-wrap'
                      : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
                  }`}
                >
                  {slideItems.map((dessert, index) => (
                    <div
                      key={dessert.name}
                      className={`group cursor-pointer ${isLastSlideWithFewerItems ? 'w-full md:w-[calc(50%-0.75rem)] lg:w-[calc(25%-1.125rem)]' : ''}`}
                      style={{
                        animation: currentIndex === slideIndex
                          ? `fadeInScale 0.6s ease-out ${index * 0.1}s both`
                          : 'none'
                      }}
                    >
                      {/* Card */}
                      <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 h-full flex flex-col">
                        {/* Image */}
                        <div className="relative overflow-hidden aspect-[4/3]">
                          <img
                            src={dessert.image}
                            alt={dessert.name}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                          {/* Floating badge */}
                          <div className="absolute top-4 right-4 w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center shadow-lg transform translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                            <i className="ri-star-fill text-white text-lg"></i>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-5 flex-1 flex flex-col">
                          <h3 className="font-cormorant text-2xl text-[#1A365D] font-bold mb-2 group-hover:text-[#D4AF37] transition-colors duration-300">
                            {dessert.name}
                          </h3>
                          <p className="text-[#64748B] text-sm leading-relaxed flex-1">
                            {dessert.description}
                          </p>

                          {/* Decorative line */}
                          <div className="mt-3 pt-3 border-t border-[#D4AF37]/20">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-[#D4AF37] font-semibold tracking-wider uppercase">Günlük Taze</span>
                              <i className="ri-cake-line text-[#D4AF37] text-lg"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Dots Navigation */}
        <div className="flex items-center justify-center gap-3 mt-10">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 cursor-pointer ${
                index === currentIndex
                  ? 'w-10 h-3 bg-[#D4AF37] rounded-full'
                  : 'w-3 h-3 bg-[#2B4C7E]/30 rounded-full hover:bg-[#2B4C7E]/50'
              }`}
              aria-label={`Sayfa ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Special note */}
      <div className="mt-12 flex justify-center relative z-10">
        <div className="bg-white/80 backdrop-blur-sm px-8 py-4 rounded-full shadow-lg border border-[#D4AF37]/20">
          <p className="text-[#1A365D] text-sm font-medium text-center flex items-center gap-2">
            <i className="ri-information-line text-[#D4AF37]"></i>
            Tatlılarımız, tazeliğini korumak ve kaliteyi en üst seviyede tutmak için haftanın belirli günlerinde üretilmektedir.
          </p>
        </div>
      </div>
    </section>
  );
});

DessertsMenu.displayName = 'DessertsMenu';

export default DessertsMenu;
